import SortIcon from '@mui/icons-material/Sort';
import {
    Box,
    Checkbox,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type ChangeEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

type ColumnAlignment = 'left' | 'center' | 'right';

// Types and Interfaces
// eslint-disable-next-line typescript/no-explicit-any -- Data can be any Row type
export interface Column<T = any> {
    id: string;
    label: string;
    _render?: (data: T) => ReactNode;
    _isSortable?: boolean;
    _width?: string | number;
    align?: ColumnAlignment;
}

/**
 * Base row interface - rows can have any properties but must have the key specified by `generateKeyFrom`
 */
export interface BaseRow {
    selected?: boolean;
    highlighted?: boolean;
    // eslint-disable-next-line typescript/no-explicit-any -- Row data can be any type
    [key: string]: any;
}

/**
 * Row type that requires a specific key property K to exist with a string value.
 * Use with DataTableProps<K> where K is the key field name (e.g., 'userId', 'prospectId').
 */
export type Row<K extends string = 'id'> = BaseRow & Record<K, string | number>;

/**
 * @deprecated Use Row<K> instead where K is your key field name (e.g., Row<'userId'>)
 */
export type RowWithId = Row<'id'>;

export interface DataTableProps<K extends string = 'id'> {
    isLoading?: boolean;
    generateKeyFrom: K;
    columns: Column[];
    rows: Row<K>[];
    rowsPerPage: number;
    page: number;
    /**
     * Total row count for TablePagination.
     * When provided, rows are treated as the current page (server-side pagination)
     * and are not sliced client-side. When omitted, rows are the full dataset and
     * DataTable slices by page/rowsPerPage (client-side pagination).
     */
    count?: number;
    isSelectable?: boolean;
    selected?: string[];
    setSelected?: (selected: string[]) => void;
    selectionMode?: 'single' | 'multiple';
    hideSelectAll?: boolean;
    handleChangePage: (event: unknown, newPage: number) => void;
    handleChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement>) => void;
    customStyles?: Record<string, unknown>;
    initialSort?: SortConfig;
    rowsPerPageOptions?: (number | { label: string; value: number })[];
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, { label: 'All', value: -1 }];

export default function DataTable<K extends string = 'id'>({
    isLoading = false,
    generateKeyFrom,
    columns,
    rows,
    rowsPerPage,
    page,
    count,
    isSelectable = false,
    selectionMode = 'multiple',
    hideSelectAll = false,
    handleChangePage,
    handleChangeRowsPerPage,
    selected,
    setSelected,
    customStyles = {},
    initialSort,
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
}: DataTableProps<K>) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isServerPaginated = count !== undefined;

    const [sortConfig, setSortConfig] = useState<SortConfig | null>(() => initialSort ?? null);

    const prevInitialSortRef = useRef<SortConfig | null>(initialSort ?? null);

    const numColumns = isSelectable ? columns.length + 1 : columns.length;

    const getColumnAlignment = (column: Column): ColumnAlignment => {
        return column.align ?? 'left';
    };

    useEffect(() => {
        // Only reset if initialSort values actually changed (deep comparison)
        const prevInitialSort = prevInitialSortRef.current;
        if (initialSort) {
            if (
                !prevInitialSort ||
                prevInitialSort.key !== initialSort.key ||
                prevInitialSort.direction !== initialSort.direction
            ) {
                setSortConfig(initialSort);
                prevInitialSortRef.current = initialSort;
            }
        } else if (prevInitialSort) {
            // If initialSort was removed, don't reset sortConfig (let user's sort persist)
            prevInitialSortRef.current = null;
        }
    }, [initialSort]);

    const sortedRows = useMemo(() => {
        const sortableRows = [...rows];
        if (sortConfig) {
            sortableRows.sort((a, b) => {
                const { key } = sortConfig;
                const aValue = a[key];
                const bValue = b[key];

                // Explicitly check for null or undefined
                if (aValue == null && bValue == null) {
                    return 0; // Both are null or undefined, consider them equal
                } else if (aValue == null) {
                    return sortConfig.direction === 'asc' ? -1 : 1; // null/undefined values go last or first
                } else if (bValue == null) {
                    return sortConfig.direction === 'asc' ? 1 : -1; // null/undefined values go last or first
                }

                // If neither value is null or undefined, proceed with comparison
                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                } else if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                } else {
                    return 0;
                }
            });
        }
        return sortableRows;
    }, [rows, sortConfig]);

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' | null = 'asc';
        if (sortConfig) {
            if (sortConfig.key === key && sortConfig.direction === 'asc') {
                direction = 'desc';
            } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
                direction = null;
            }
        }
        setSortConfig(direction ? { key, direction } : null);
    };

    const handleSelect = (id: string) => {
        if (selected) {
            if (selectionMode === 'single') {
                const isAlreadySelected = selected.includes(id);
                setSelected?.(isAlreadySelected ? [] : [id]);
                return;
            }

            const selectedIndex = selected.indexOf(id);
            let newSelected: string[] = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, id);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1),
                );
            }

            setSelected?.(newSelected);
        }
    };

    const handleSelectAllClick = () => {
        if (selected && selectionMode === 'multiple') {
            const newSelected =
                selected.length === rows.length ? [] : rows.map((row) => row[generateKeyFrom]);
            setSelected?.(newSelected);
        }
    };

    return isMobile ? (
        <Box>
            {sortedRows.map((row) => (
                <Box
                    key={row[generateKeyFrom]}
                    sx={{
                        border: `1px solid ${theme.palette.neutral.N_200}`,
                        borderRadius: 2,
                        padding: 2,
                        marginBottom: 2,
                        backgroundColor: '#fff',
                        ...(row.highlighted
                            ? {
                                  animation: 'highlightedDataTableRowPulse 1.25s ease-in-out 4',
                                  '@keyframes highlightedDataTableRowPulse': {
                                      '0%': { backgroundColor: theme.palette.primary.P_50 },
                                      '50%': { backgroundColor: theme.palette.primary.P_100 },
                                      '100%': { backgroundColor: theme.palette.primary.P_50 },
                                  },
                              }
                            : {}),
                    }}
                >
                    {columns.map((column) => (
                        <Box
                            key={column.id}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 8px',
                                borderRadius: 1,
                                mb: 1,
                                overflowX: 'auto',
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{ flex: 1, color: theme.palette.text.secondary }}
                            >
                                {column.label}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    flex: 1,
                                    textAlign: 'left',
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {column._render ? column._render(row) : row[column.id]}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    ) : (
        <TableContainer
            sx={{
                border: `1px solid ${theme.palette.neutral.N_200}`,
                ...customStyles,
            }}
            component={Paper}
        >
            <Table>
                <TableHead>
                    <TableRow>
                        {isSelectable && (
                            <TableCell padding="checkbox">
                                {!hideSelectAll && (
                                    <Checkbox
                                        checked={selected?.length === rows.length}
                                        onChange={handleSelectAllClick}
                                        inputProps={{ 'aria-label': 'select all rows' }}
                                    />
                                )}
                            </TableCell>
                        )}
                        {columns.map((column: Column) => (
                            <TableCell
                                key={column.id}
                                width={column._width}
                                align={getColumnAlignment(column)}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItem: 'center',
                                        gap: '5px',
                                        justifyContent:
                                            getColumnAlignment(column) === 'right'
                                                ? 'flex-end'
                                                : getColumnAlignment(column),
                                    }}
                                >
                                    {column._isSortable ? (
                                        <TableSortLabel
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                '& .MuiTableSortLabel-icon': {
                                                    fontSize: 16,
                                                    width: 16,
                                                    height: 16,
                                                },
                                            }}
                                            active={
                                                sortConfig ? sortConfig.key === column.id : false
                                            }
                                            direction={sortConfig ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort(column.id)}
                                            IconComponent={SortIcon}
                                        >
                                            <Typography variant="body2">{column.label}</Typography>
                                        </TableSortLabel>
                                    ) : (
                                        <Typography variant="body2">{column.label}</Typography>
                                    )}
                                </Box>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                {/* table body */}
                {!isLoading && (
                    <TableBody>
                        {(isServerPaginated
                            ? sortedRows
                            : rowsPerPage > 0
                              ? sortedRows.slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage,
                                )
                              : sortedRows
                        ).map((row) => (
                            <TableRow
                                key={row[generateKeyFrom]}
                                selected={
                                    selected && selected?.indexOf(row[generateKeyFrom]) !== -1
                                }
                                sx={
                                    row.highlighted
                                        ? {
                                              animation:
                                                  'highlightedDataTableRowPulse 1.25s ease-in-out 4',
                                              backgroundColor: theme.palette.primary.P_50,
                                              '& > td': { backgroundColor: 'inherit' },
                                              '@keyframes highlightedDataTableRowPulse': {
                                                  '0%': {
                                                      backgroundColor: theme.palette.primary.P_50,
                                                  },
                                                  '50%': {
                                                      backgroundColor: theme.palette.primary.P_100,
                                                  },
                                                  '100%': {
                                                      backgroundColor: theme.palette.primary.P_50,
                                                  },
                                              },
                                          }
                                        : undefined
                                }
                            >
                                {isSelectable && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={
                                                selected &&
                                                selected?.indexOf(row[generateKeyFrom]) !== -1
                                            }
                                            onChange={() => handleSelect(row[generateKeyFrom])}
                                            inputProps={{
                                                'aria-label': `select row ${row[generateKeyFrom]}`,
                                            }}
                                        />
                                    </TableCell>
                                )}
                                {columns.map((column) => (
                                    <TableCell key={column.id} align={getColumnAlignment(column)}>
                                        {column._render ? column._render(row) : row[column.id]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                )}
                {isLoading && (
                    <TableBody>
                        {Array.from(
                            { length: rowsPerPage !== -1 ? rowsPerPage : 10 },
                            (_, index) => (
                                <TableRow key={index}>
                                    {Array.from({ length: numColumns }, (_, index) => (
                                        <TableCell key={index}>
                                            <Skeleton />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ),
                        )}
                    </TableBody>
                )}
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={rowsPerPageOptions}
                            count={count ?? rows.length}
                            labelDisplayedRows={({ from, to, count: total }) =>
                                `Showing ${from}-${to} of ${total}`
                            }
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: { 'aria-label': 'rows per page' },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
}
